// Verknüpfungen eines Kontakts – Abfragen und Namensauflösung wie bisher in KontaktVerknuepft.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { sichereHttpUrl } from '../url';
import { ladeVerknuepfungen, loeseVerknuepfung } from './api';
import type { KontaktQuelle, RohLink } from './types';
import { verknuepfungAnzeige } from './verknuepfung';

export interface VerknuepfungZeile {
  zielTyp: string;
  /** aufgelöster echter Name (z. B. „Am Einweg 2 A“) */
  titel: string;
  /** lesbare Beziehung (z. B. „Mietverhältnis“) */
  beziehung: string;
  /** Zusatz (z. B. Adresse) */
  extra?: string;
  /** Klick-Ziel: Objekt-Detail */
  objektId?: string;
  /** Klick-Ziel: Dokument in neuem Tab */
  url?: string;
  /** entity_links.id – für „Verknüpfung aufheben“ (fehlt bei abgeleiteten Zeilen) */
  linkId?: string;
}

export const ZIELTYP_LABEL: Record<string, string> = {
  objekt: 'Objekt', einheit: 'Einheit', eigentuemer: 'Eigentümer',
  mieter: 'Mieter', dienstleister: 'Dienstleister', email: 'E-Mail', ticket: 'Ticket', kontakt: 'Kontakt', dokument: 'Dokument',
};

const LINKTYP_LABEL: Record<string, string> = {
  mietverhaeltnis: 'Mietverhältnis',
  eigentum: 'Eigentum',
  verwaltung: 'Verwaltung',
  wartung: 'Wartung',
  auftrag: 'Auftrag',
};

export function beziehungLabel(t?: string): string {
  const k = (t || '').toLowerCase();
  if (LINKTYP_LABEL[k]) return LINKTYP_LABEL[k];
  return k ? k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Verknüpft';
}

interface AdressTeile { strasse?: string | null; hausnummer?: string | null; plz?: string | null; stadt?: string | null }
function adresse(o: AdressTeile): string {
  return [[o.strasse, o.hausnummer].filter(Boolean).join(' '), [o.plz, o.stadt].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
}

type Name = { titel: string; extra?: string; objektId?: string; url?: string };

async function ladeZeilen(quelle: KontaktQuelle, id: string): Promise<VerknuepfungZeile[]> {
  const links: RohLink[] = await ladeVerknuepfungen(quelle, id);
  const roh = links
    .map((l) => { const a = verknuepfungAnzeige(l, { typ: quelle, id }); return a ? { ...a, linkTyp: l.link_type, linkId: l.id } : null; })
    .filter((x): x is { zielTyp: string; zielId: string; label: string; linkTyp: string; linkId: string } => x !== null);

  // Echte Namen je Zieltyp auflösen (Batch pro Typ).
  const namen = new Map<string, Name>();
  const idsVon = (t: string) => roh.filter((r) => r.zielTyp === t).map((r) => r.zielId);

  const objIds = idsVon('objekt');
  if (objIds.length) {
    const { data } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer, plz, stadt').in('id', objIds);
    for (const o of (data ?? []) as (AdressTeile & { id: string; bezeichnung: string | null })[]) {
      namen.set(`objekt:${o.id}`, { titel: o.bezeichnung || 'Objekt', extra: adresse(o) || undefined, objektId: o.id });
    }
  }
  const einheitIds = idsVon('einheit');
  if (einheitIds.length) {
    const { data } = await supabase.from('objekt_einheiten').select('id, bezeichnung, objekt_id').in('id', einheitIds);
    for (const e of (data ?? []) as { id: string; bezeichnung: string | null; objekt_id: string | null }[]) {
      namen.set(`einheit:${e.id}`, { titel: e.bezeichnung || 'Einheit', objektId: e.objekt_id || undefined });
    }
  }
  const ticketIds = idsVon('ticket');
  if (ticketIds.length) {
    const { data } = await supabase.from('tickets').select('id, titel').in('id', ticketIds);
    for (const t of (data ?? []) as { id: string; titel: string | null }[]) namen.set(`ticket:${t.id}`, { titel: t.titel || 'Ticket' });
  }
  const dokIds = idsVon('dokument');
  if (dokIds.length) {
    const { data } = await supabase.from('dokumente').select('id, titel, datei_name, datei_url, kategorie').in('id', dokIds);
    for (const d of (data ?? []) as { id: string; titel: string | null; datei_name: string | null; datei_url: string | null; kategorie: string | null }[]) {
      namen.set(`dokument:${d.id}`, {
        titel: d.titel || d.datei_name || 'Dokument',
        extra: d.kategorie || undefined,
        url: sichereHttpUrl(d.datei_url) ?? undefined,
      });
    }
  }
  // Personen (Name aus der einheitlichen Kontakte-View)
  for (const pTyp of ['mieter', 'eigentuemer', 'dienstleister', 'kontakt']) {
    const pIds = idsVon(pTyp);
    if (!pIds.length) continue;
    const { data } = await supabase.from('kontakte_unified').select('quelle_id, anzeigename').eq('quelle', pTyp).in('quelle_id', pIds);
    for (const p of (data ?? []) as { quelle_id: string; anzeigename: string | null }[]) {
      namen.set(`${pTyp}:${p.quelle_id}`, { titel: p.anzeigename || ZIELTYP_LABEL[pTyp] || pTyp });
    }
  }

  const result: VerknuepfungZeile[] = roh.map((r) => {
    const n = namen.get(`${r.zielTyp}:${r.zielId}`);
    return {
      zielTyp: r.zielTyp,
      titel: n?.titel ?? ZIELTYP_LABEL[r.zielTyp] ?? r.zielTyp,
      beziehung: beziehungLabel(r.linkTyp),
      extra: n?.extra,
      objektId: n?.objektId ?? (r.zielTyp === 'objekt' ? r.zielId : undefined),
      url: n?.url,
      linkId: r.linkId,
    };
  });

  // Mieter: Einheit steht auf dem Mieter (nicht in entity_links) → als eigene Zeile ergänzen.
  if (quelle === 'mieter') {
    const { data: m } = await supabase.from('mieter').select('einheit, objekt_id').eq('id', id).maybeSingle();
    const zeile = m as { einheit: string | null; objekt_id: string | null } | null;
    if (zeile?.einheit) result.push({ zielTyp: 'einheit', titel: zeile.einheit, beziehung: 'Wohneinheit', objektId: zeile.objekt_id || undefined });
  }
  return result;
}

export function useKontaktVerknuepfungen(quelle: KontaktQuelle, id: string) {
  const [zeilen, setZeilen] = useState<VerknuepfungZeile[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [runde, setRunde] = useState(0);
  const [busyLinkId, setBusyLinkId] = useState<string | null>(null);

  useEffect(() => {
    let abbruch = false;
    setLaedt(true);
    ladeZeilen(quelle, id)
      .then((r) => { if (!abbruch) { setZeilen(r); setLaedt(false); } })
      .catch(() => { if (!abbruch) { setZeilen([]); setLaedt(false); } });
    return () => { abbruch = true; };
  }, [quelle, id, runde]);

  /** Hebt eine Verknüpfung auf; liefert die Fehlermeldung oder null. */
  const loese = useCallback(async (z: VerknuepfungZeile): Promise<string | null> => {
    if (!z.linkId) return null;
    setBusyLinkId(z.linkId);
    try {
      await loeseVerknuepfung(z.linkId);
      setRunde((k) => k + 1);
      return null;
    } catch (err: unknown) {
      return (err as { message?: string } | null)?.message || 'Unbekannt';
    } finally {
      setBusyLinkId(null);
    }
  }, []);

  return { zeilen, laedt, busyLinkId, loese };
}
